/**
 * BLOCKUSERPARAMETER parser — DYN-4 Phase 2a (Fix 3).
 *
 * AutoCAD 2018+ emits the `BLOCKUSERPARAMETER` class (and its prefixed
 * twin `ACDBBLOCKUSERPARAMETER`) as the glue between a dynamic block's
 * authoring graph and an `ACDBASSOCVARIABLE` in the OBJECTS section.
 * Each user parameter carries:
 *   - a self handle (group 5),
 *   - the standard AcDbEvalExpr / AcDbBlockElement / AcDbBlockParameter
 *     / AcDbBlock1PtParameter envelope,
 *   - an `AcDbBlockUserParameter` subclass marker,
 *   - a group 330 immediately after the marker pointing at the
 *     associated variable,
 *   - an optional group 301 / 300 with a fallback user-visible name,
 *   - a group 309 trailer ("AcDbEvalVariant NULL = -9999") we ignore.
 *
 * In real-world АБИ1.dxf ibeam (handle 4DDB), three user parameters are
 * present: "Двутавр" (key column driver), "Площадь" (driven output),
 * "Масса" (driven output). Each references an ACDBASSOCVARIABLE inside
 * the block's associative network.
 *
 * This module's only responsibility is to decode one BLOCKUSERPARAMETER
 * DXF object into a `UserParameter` typed record. The caller is
 * responsible for:
 *   - Pre-scanning OBJECTS for ACDBASSOCVARIABLE records (see
 *     `packages/dxf-io/src/assocVariableParser.ts`).
 *   - Wiring the parsed `UserParameter[]` into `DynamicBlockMetadata.parameters`
 *     (happens in DYN-4 Phase 3 — NOT in this phase).
 *
 * Paired with: (no writer yet — DYN-4 Phase 3 adds the symmetric emitter.)
 */
import type {
  AssocVariable,
  DxfDynamicBlockObject,
  DxfGroup,
} from "@dxf-ai-editor/dxf-io";

import type { UserParameter } from "./dynamicBlockParameters";
import { firstGroup, firstInt } from "./dxfBridge.dynamic.shared";

/**
 * Decode a `BLOCKUSERPARAMETER` / `ACDBBLOCKUSERPARAMETER` OBJECTS record
 * into a typed `UserParameter`.
 *
 * @param obj             The raw dynamic-block object (type already confirmed
 *                        to be one of the user-parameter classes via
 *                        `canonicalDynamicBlockClass`).
 * @param assocVariables  Pre-scanned `Map<handle, AssocVariable>` from
 *                        `collectAssocVariables`. May be empty — the
 *                        parser falls back to a synthetic default when
 *                        the reference cannot be resolved.
 */
export function parseUserParameter(
  obj: DxfDynamicBlockObject,
  assocVariables: Map<string, AssocVariable>,
): UserParameter {
  const rawGroups = obj.rawGroups;

  // Group 330 AFTER the AcDbBlockUserParameter subclass marker is the
  // ACDBASSOCVARIABLE handle. Earlier 330s are ACAD_REACTORS / parent
  // block references that must not be confused with the association.
  const assocHandle = findAssocHandle(rawGroups);
  const variable =
    assocHandle !== null ? assocVariables.get(assocHandle) : undefined;

  // Name resolution cascade:
  //   1. Associated variable's name (authoritative — matches AutoCAD UI).
  //   2. Group 301 — secondary name string present in some AutoCAD builds.
  //   3. Group 300 — primary AcDbBlockElement name field.
  //   4. Synthetic fallback so validateDynamicMetadata does not reject.
  const resolvedName =
    variable?.name ||
    firstGroup(rawGroups, 301)?.trim() ||
    firstGroup(rawGroups, 300)?.trim() ||
    `user_${obj.handle}`;

  // Default value: prefer the associated variable's current value (this
  // is what AutoCAD displays in the Properties Palette as the "default").
  // When the association is missing, seed with 0 / empty-string so the
  // schema parse does not fail.
  const defaultValue: UserParameter["defaultValue"] =
    variable?.currentValue ?? 0;

  const valueType: UserParameter["valueType"] =
    variable?.valueType ?? "unknown";

  // `showGrip` — BLOCKUSERPARAMETER uses code 281 on the
  // AcDbBlock1PtParameter subclass (value 0 = hidden, 1 = shown). Default
  // to true so UI renders the parameter unless the DXF explicitly hides it.
  const showGrip = (firstInt(rawGroups, 281) ?? 1) !== 0;

  const result: UserParameter = {
    kind: "user",
    id: obj.handle,
    name: resolvedName,
    label: firstGroup(rawGroups, 3)?.trim() ?? "",
    description: firstGroup(rawGroups, 4)?.trim() ?? "",
    chainActions: (firstInt(rawGroups, 280) ?? 0) === 1,
    showGrip,
    associatedVariableHandle: assocHandle ?? "",
    variableName: variable?.name ?? "",
    valueType,
    defaultValue,
  };
  if (variable?.expression !== undefined) {
    result.expression = variable.expression;
  }
  return result;
}

/**
 * Conservative heuristic for locating the ACDBASSOCVARIABLE handle
 * referenced by a BLOCKUSERPARAMETER body. AutoCAD emits multiple group
 * 330 references inside the same object (owner block, reactors, parent
 * entity, associative variable), so we anchor our search on the
 * `AcDbBlockUserParameter` subclass marker — the first 330 AFTER that
 * marker is the associative reference.
 *
 * Returns `null` when the marker is absent or no 330 follows it — the
 * caller then falls back to synthetic defaults.
 */
export function findAssocHandle(rawGroups: DxfGroup[]): string | null {
  let pastMarker = false;
  for (const g of rawGroups) {
    if (g.code === 100 && g.value === "AcDbBlockUserParameter") {
      pastMarker = true;
      continue;
    }
    if (pastMarker && g.code === 330) {
      const v = g.value.trim();
      return v.length > 0 ? v : null;
    }
  }
  return null;
}
